import { authError, isAuthenticated } from '../state/auth';

export interface SessionConfig {
    checkInterval: number; // milliseconds
    warningThreshold: number; // minutes before expiration to show warning
    lockName: string;
}

export const DEFAULT_SESSION_CONFIG: SessionConfig = {
    checkInterval: 5 * 60 * 1000, // 5 minutes instead of 1 minute
    warningThreshold: 10, // 10 minutes warning before expiration
    lockName: 'session-check-lock',
};

export class SessionManager {
    private config: SessionConfig;
    private intervalId: NodeJS.Timeout | null = null;
    private warningShown = false;
    private lastSuccessfulCheck = Date.now();
    private isSigningOut = false;
    private lastTokenRefresh = Date.now();
    private tokenExpiresAt: number | null = null;

    constructor(config: Partial<SessionConfig> = {}) {
        this.config = { ...DEFAULT_SESSION_CONFIG, ...config };
    }

    start(): void {
        this.isSigningOut = false;
        this.warningShown = false;
        this.lastSuccessfulCheck = Date.now();
        this.lastTokenRefresh = Date.now();

        // Initial check after a short delay to let the app initialize
        setTimeout(() => this.performCheck(), 2000);

        // Set up regular interval checks
        this.intervalId = setInterval(
            () => this.performCheck(),
            this.config.checkInterval,
        );

        // Add visibility change listener
        document.addEventListener('visibilitychange', this.handleVisibilityChange);

        // Add online/offline listeners
        window.addEventListener('online', this.handleOnline);
        window.addEventListener('offline', this.handleOffline);

        // Add activity listeners for automatic session extension
        this.addActivityListeners();
    }

    stop(): void {
        this.isSigningOut = true;
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }

        // Remove event listeners
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        window.removeEventListener('online', this.handleOnline);
        window.removeEventListener('offline', this.handleOffline);
    }

    private async performCheck(): Promise<void> {
        // Skip check if we're signing out or recently refreshed
        if (this.isSigningOut) return;

        const timeSinceLastRefresh = Date.now() - this.lastTokenRefresh;
        // Skip check if we refreshed less than 2 minutes ago
        if (timeSinceLastRefresh < 2 * 60 * 1000) {
            console.debug('Skipping session check, recently refreshed');
            return;
        }

        // Check if token is expiring soon and proactively refresh
        if (this.isTokenExpiringSoon()) {
            console.info('Token expiring soon, checking session...');
        }

        if (this.isLocksApiSupported()) {
            try {
                const result = await navigator.locks.request(
                    this.config.lockName,
                    { ifAvailable: true },
                    async (lock) => {
                        if (!lock) {
                            console.debug(
                                'Session check already in progress in another tab',
                            );
                            return false;
                        }
                        await this.checkSession();
                        return true;
                    },
                );

                if (result !== true) {
                    return;
                }
            } catch (error) {
                console.error('Lock acquisition error:', error);
                await this.checkSession();
            }
        } else {
            await this.checkSession();
        }
    }

    private async checkSession(retries = 2): Promise<void> {
        // Don't check session if we're signing out
        if (this.isSigningOut) {
            return;
        }

        console.debug('SessionManager: Checking session, retries left:', retries);

        try {
            // First try the lighter-weight /user endpoint
            const response = await fetch('/user', {
                method: 'GET',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                },
            });

            console.debug('SessionManager: Session check response:', response.status);

            if (response.ok) {
                const userData = await response.json();
                console.debug('SessionManager: Session valid, user:', userData.email);

                // The /user endpoint automatically refreshes tokens if needed
                // Update our tracking
                this.lastTokenRefresh = Date.now();
                this.updateTokenExpiration();
                this.handleSuccessfulCheck();
                return;
            }

            if (
                response.status === 401 ||
                response.status === 403 ||
                response.status === 404
            ) {
                // Session truly expired, need to re-authenticate
                console.debug(
                    'SessionManager: Session expired, status:',
                    response.status,
                );
                this.handleSessionExpired();
                return;
            }

            // 500 errors might be temporary
            console.debug(
                'SessionManager: Session check failed with status:',
                response.status,
            );
            if (retries > 0) {
                await this.delay(2000 * (3 - retries)); // Exponential backoff
                await this.checkSession(retries - 1);
            } else {
                this.handleCheckError('Unable to verify session');
            }
        } catch (error) {
            console.error('Network error checking session:', error);

            // Check if we've been offline for too long
            const timeSinceLastCheck = Date.now() - this.lastSuccessfulCheck;
            if (timeSinceLastCheck > 60 * 60 * 1000) {
                // 1 hour instead of 30 min
                this.handleSessionExpired();
                return;
            }

            if (retries > 0) {
                await this.delay(3000 * (3 - retries)); // Exponential backoff
                await this.checkSession(retries - 1);
            } else {
                this.handleCheckError('Network connection issue');
            }
        }
    }

    private handleSuccessfulCheck(): void {
        this.lastSuccessfulCheck = Date.now();
        this.warningShown = false;
        if (authError.value) {
            authError.value = null;
        }
        console.debug('Session check successful');
    }

    private handleSessionExpired(): void {
        if (this.isSigningOut) {
            return; // Don't show error messages during signout
        }

        console.error('Session expired');
        isAuthenticated.value = false;
        authError.value = 'Your session has expired. Redirecting to login...';

        // Give user time to see the message
        setTimeout(() => {
            window.location.href = '/auth';
        }, 2000);
    }

    private handleCheckError(message: string): void {
        if (!this.warningShown && !this.isSigningOut && isAuthenticated.value) {
            authError.value = `${message}. Please check your connection.`;
            this.warningShown = true;
        }
    }

    private isLocksApiSupported(): boolean {
        return 'locks' in navigator && typeof navigator.locks?.request === 'function';
    }

    private delay(ms: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    private updateTokenExpiration(): void {
        try {
            // Try to parse the access_token cookie to get expiration
            const accessToken = this.getCookie('access_token');
            if (accessToken) {
                // Decode JWT without verification (since we're just reading exp claim)
                const parts = accessToken.split('.');
                if (parts.length === 3) {
                    const payload = JSON.parse(atob(parts[1]));
                    if (payload.exp) {
                        this.tokenExpiresAt = payload.exp * 1000; // Convert to milliseconds
                        console.debug('Token expires at:', new Date(this.tokenExpiresAt));
                    }
                }
            }
        } catch (error) {
            console.debug('Could not parse token expiration:', error);
        }
    }

    private getCookie(name: string): string | null {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop()?.split(';').shift() || null;
        }
        return null;
    }

    public isTokenExpiringSoon(): boolean {
        if (!this.tokenExpiresAt) return false;

        const now = Date.now();
        const timeUntilExpiry = this.tokenExpiresAt - now;
        const warningThresholdMs = this.config.warningThreshold * 60 * 1000;

        return timeUntilExpiry > 0 && timeUntilExpiry <= warningThresholdMs;
    }

    public getTimeUntilExpiry(): number | null {
        if (!this.tokenExpiresAt) return null;
        return Math.max(0, this.tokenExpiresAt - Date.now());
    }

    private handleVisibilityChange = (): void => {
        if (!document.hidden && !this.isSigningOut) {
            const timeSinceLastCheck = Date.now() - this.lastSuccessfulCheck;
            // If tab becomes visible and it's been more than 10 minutes since last check
            if (timeSinceLastCheck > 10 * 60 * 1000) {
                console.info('Tab became visible after long period, checking session...');
                this.performCheck();
            }
        }
    };

    private addActivityListeners(): void {
        // Debounced activity handler
        let activityTimeout: NodeJS.Timeout | null = null;
        const handleActivity = () => {
            if (activityTimeout) clearTimeout(activityTimeout);

            activityTimeout = setTimeout(() => {
                const timeSinceLastCheck = Date.now() - this.lastSuccessfulCheck;
                // Only check if it's been more than 10 minutes
                if (timeSinceLastCheck > 10 * 60 * 1000 && !this.isSigningOut) {
                    console.debug('User activity detected, checking session...');
                    this.performCheck();
                }
            }, 1000); // Debounce for 1 second
        };

        // Listen for user activity
        document.addEventListener('mousedown', handleActivity);
        document.addEventListener('keydown', handleActivity);
        document.addEventListener('touchstart', handleActivity);
    }

    private handleOnline = (): void => {
        console.info('Network connection restored, checking session...');
        if (authError.value) {
            authError.value = null;
        }
        this.performCheck();
    };

    private handleOffline = (): void => {
        console.info('Network connection lost');
        if (!authError.value) {
            authError.value =
                "Network connection lost. We'll try to reconnect when you're back online.";
        }
    };
}

// Export a singleton instance
export const sessionManager = new SessionManager();
